import { t } from "@lingui/macro";
import { Button } from "@stylingcv/ui";
import { Link, useLocation, useNavigate, useRouteError } from "react-router-dom";

import { LocaleProvider } from "@/client/providers/locale";

import { createLocalizedNavigation } from "../../utils/url-locales";

type RouterError = {
  statusText?: string;
  message?: string;
  status: number;
  data: string;
} & Error;

const getErrorMessage = (status: number, message?: string) => {
  // Special handling for JSON parsing errors
  if (message && message.includes("Unexpected token") && message.includes("JSON")) {
    return t`Invalid JSON format. The data might contain invalid characters or formatting issues.`;
  }

  switch (status) {
    case 404: {
      return t`The page you're looking for doesn't exist.`;
    }
    case 403: {
      return t`You don't have permission to access this page.`;
    }
    case 500: {
      return t`An internal server error occurred.`;
    }
    case 401: {
      return t`You are not authorized to access this page.`;
    }
    case 400: {
      return t`The request was invalid.`;
    }
    default: {
      return t`An unexpected error occurred.`;
    }
  }
};

export const ErrorPage = () => {
  const error = useRouteError() as RouterError | null;
  const navigate = useNavigate();
  const location = useLocation();
  const { getLocalizedHref } = createLocalizedNavigation(location.pathname);

  // Detect session expired / auth errors
  const isSessionExpired = error?.message?.toLowerCase().includes("session expired") ||
    error?.message?.toLowerCase().includes("please login");

  // If no status is provided and it's not a known error type, show as generic error instead of 500
  const statusCode = error?.status ?? (isSessionExpired ? 401 : 0);

  // Special handling for JSON parsing errors
  const isJsonError =
    error?.message?.includes("JSON") && error?.message?.includes("Unexpected token");

  const handleTryAgain = () => {
    // Try to navigate back if it's a JSON parsing error
    if (isJsonError) {
      navigate(-1);
    }
  };

  // Determine display title
  const getDisplayTitle = () => {
    if (isJsonError) return t`JSON Error`;
    if (isSessionExpired) return t`Session Expired`;
    if (statusCode === 0) return t`Error`;
    return t`Error ${statusCode}`;
  };

  return (
    <LocaleProvider>
      <main className="flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <h4 className="flex flex-col text-4xl font-bold text-white">
            <span>{getDisplayTitle()}</span>
            {error?.statusText && <span className="text-base font-normal">{error.statusText}</span>}
          </h4>

          <p className="break-words text-sm text-gray-500">
            {error?.data ?? error?.message ?? getErrorMessage(statusCode, error?.message)}
          </p>

          <div className="flex gap-4">
            {isSessionExpired ? (
              <Button asChild className="inline-block pt-2">
                <Link to={getLocalizedHref("/auth/login")}>{t`Log in again`}</Link>
              </Button>
            ) : (
              <Button asChild className="inline-block pt-2">
                <Link to={getLocalizedHref("/")}>{t`Go to home`}</Link>
              </Button>
            )}

            {isJsonError && (
              <Button variant="outline" className="inline-block pt-2" onClick={handleTryAgain}>
                {t`Go back and try again`}
              </Button>
            )}
          </div>
        </div>
      </main>
    </LocaleProvider>
  );
};
